import React, { useState } from 'react';
import styled from '@emotion/styled/macro';
import Translate from '@utils/translation-functions';
import vars from '@variables';
import { FlexibleLinkAtom } from '@atoms/link-atoms/link-atoms';
import { flipInX } from 'react-animations';
import { getImageParameters } from '../../mixins';
import { GTM_ID } from '../../../../common/constants/gtmIds';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { keyframes } from '@emotion/react';
import { LogoCardProps } from './logo-card.types';
import { PAtom } from '@atoms/text-atoms/text-atoms';

const LogoCard: React.FC<LogoCardProps> = ({
  name,
  type,
  url,
  linkText,
  gatsbyImageData,
  logoSrc,
  logoFormat,
  logoAlt,
  className,
  translate = true,
}) => {
  const [currentSide, setCurrentSide] = useState('front');
  const gatsbyImage = getImage(gatsbyImageData);
  if (logoSrc && name) {
    return (
      <div
        className={className}
        data-testid="logo-card"
        onClick={() =>
          currentSide === 'front'
            ? setCurrentSide('back')
            : setCurrentSide('front')
        }
        onKeyDown={() =>
          currentSide === 'front'
            ? setCurrentSide('back')
            : setCurrentSide('front')
        }
        role="button"
        tabIndex={0}
        // TODO: Remove CTA link from onClick area (useEffect with event listener?)
      >
        <div
          className={`front ${
            currentSide === 'front' ? 'display-block' : 'display-none'
          }`}
          data-testid="logo-card-front"
        >
          {gatsbyImage &&
          logoFormat !== 'image/gif' &&
          logoFormat !== 'image/svg+xml' ? (
            <GatsbyImage
              image={gatsbyImage}
              alt={logoAlt ? logoAlt : `${name} logo`}
              className="gatsby-image-pos"
            />
          ) : (
            <div className="gatsby-image-wrapper gatsby-image-wrapper-constrained gatsby-image-pos">
              {logoSrc && (
                <img
                  src={getImageParameters(
                    logoSrc,
                    vars.defaultImageFormat,
                    300,
                    300,
                    'pad'
                  )}
                  alt={logoAlt ? logoAlt : `${name} logo`}
                />
              )}
            </div>
          )}
        </div>

        <div className="back">
          <FlexibleLinkAtom url={url} gtmId={GTM_ID.LOGO_BLOCK}>
            {name && <PAtom className="name">{name}</PAtom>}
            {type && <PAtom className="type">{type}</PAtom>}
            {url && (
              <PAtom className="link-text">
                {linkText || Translate('learn more', translate)}
              </PAtom>
            )}
          </FlexibleLinkAtom>
        </div>
      </div>
    );
  } else {
    return null;
  }
};

const StyledLogoCard = styled(LogoCard)`
  width: 100%;
  .front {
    display: none;
  }
  .back {
    display: block;
    ${vars.fontSize1}
    padding: ${vars.spacingXs} ${vars.spacingM};
    &::first-letter {
      text-transform: uppercase;
    }
    .name {
      padding: 0;
    }
    .type {
      font-weight: ${vars.fontWeightBold};
      color: ${vars.transparentCoreGrey} !important;
    }
    ${FlexibleLinkAtom} {
      text-decoration: none;
    }
    .link-text {
      display: none;
    }
  }

  @media screen and (min-width: ${vars.breakPointThreeLogos}) {
    width: ${vars.logoCardSize};
    height: ${vars.logoCardSize};
    position: relative;
    overflow: hidden;

    .front,
    .back {
      position: absolute;

      border: 1px solid ${vars.borderGrey};
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }

    .front {
      z-index: 60;
      display: flex;
      align-items: center;
      justify-content: center;
      background: white;
      img {
        width: 100%;
        max-height: 100%;
        object-fit: cover;
      }
    }

    .back {
      z-index: 50;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;
      height: 100%;
      padding: 0 ${vars.spacingS};

      ${FlexibleLinkAtom} {
        border: ${vars.logoCardBackBorder} solid ${vars.transparentBorderGrey};
        display: flex;
        flex-direction: column;
        position: absolute;
        width: 100%;
        height: 100%;
        cursor: ${({ url }) => (url ? 'pointer' : 'auto')};
        text-decoration: none;
        align-items: center;
        justify-content: center;
        text-align: center;
        background: white;
        ${PAtom} {
          color: ${vars.coreDarkGrey};
          background: transparent !important;
          ${vars.fontSize2}
          padding: 0 ${vars.spacingS} 0 ${vars.spacingS};
          &::first-letter {
            text-transform: uppercase;
          }
          .name {
            margin: 0 ${vars.spacingS} 0 ${vars.spacingS};
            color: black;
            padding-bottom: ${({ linkText }) =>
              linkText ? vars.spacingL : null};
          }
          .type {
            &::first-letter {
              text-transform: uppercase;
            }
            padding-bottom: ${({ linkText }) =>
              linkText ? vars.spacingL : null};
            font-weight: ${vars.fontWeightBold};
            color: ${vars.fadedTextGrey};
          }
        }
        .link-text {
          display: block;
          &::first-letter {
            text-transform: uppercase;
          }
          position: absolute;
          bottom: -${vars.logoCardBackBorder};
          left: 0;
          width: 100%;
          padding: ${vars.spacingS} 0;
          margin: 0;
          background: ${vars.transparentBorderGrey} !important;
          font-weight: ${vars.fontWeightBold};
          ${vars.fontSize2}
          color: black;
        }
      }
    }

    /* Use hover to hide the front on devices allowing hover */
    @media (hover: hover) {
      &:hover {
        .front {
          display: none;
        }
        .back {
          ${FlexibleLinkAtom} {
            animation: 0.5s ${keyframes`${flipInX}`} 1;
          }
          animation: 0.5s ${keyframes`${flipInX}`} 1;
        }
      }
    }

    /* Use click to add (display: none) on devices without hover */
    @media (hover: none) {
      .display-none {
        display: none;
      }
    }
  }
`;

export default StyledLogoCard;
